import { iconNavDots } from '@bsds-lib-component/icons/icons';
import { BsdsIcon } from '@bsds-lib-component/react';
import React, { useEffect, useRef, useState } from 'react';

import type { ListaSubestipulanteItem } from '../../../types/vida/subEstipulante/subEstipulanteType';
import type { SubEstipulantesTableProps } from '../../../types/vida/table/subEstipulantesTableType';
import { useSubEstipulanteInfo } from '../../../hooks/vida/useSubEstipulanteInfo';
import { formatSubestipulanteCodNome } from '../../../utils/subestipulanteUtil';
import SubEstipulanteResumoModal from '../../pagesComponents/vidaPageComponents/modal/SubEstipulanteResumoModal';
import Pagination from '../../pagination/Pagination';
import Tooltip from '../../tooltip/Tooltip';
import TableMenuPortal from '../shared/TableMenuPortal';
import { TABLE_MENU_ITEM_BUTTON_CLASS } from '../shared/tableMenuUtils';

const getRowMenuId = (row: ListaSubestipulanteItem): string =>
  `${row.identSubestipulanteOut}-${row.codSubestipulanteOut}`;

const SubEstipulantesTable: React.FC<SubEstipulantesTableProps> = ({
  items,
  formatVigencia,
  formatDocumento,
  numeroApolice,
  onViewSegurados,
  onViewProponentes,
  onInfoSubEstipulante,
  subestipulanteResumo: subestipulanteResumoFromParent,
  emptyMessage = 'Nenhum Sub-Estipulante encontrado',
  currentPage,
  totalPages,
  totalItems,
  onPageChange,
}) => {
  const fallbackSub = useSubEstipulanteInfo();
  const subestipulanteResumo = subestipulanteResumoFromParent ?? fallbackSub.resumo;
  const hasSubEstipulanteDetailActions = Boolean(onInfoSubEstipulante);
  const hasRowActions = Boolean(
    onViewSegurados || onViewProponentes || hasSubEstipulanteDetailActions,
  );
  const columnCount = hasRowActions ? 7 : 6;

  const formatNomeComCodigo = (row: ListaSubestipulanteItem): string =>
    formatSubestipulanteCodNome(row.codSubestipulanteOut, row.nomeSubestipulanteOut);

  const [openItemMenuId, setOpenItemMenuId] = useState<string | null>(null);
  const itemMenuRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const itemMenuButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openItemMenuId === null) {
        return;
      }

      const target = event.target as Node;
      const itemMenuRef = itemMenuRefs.current.get(openItemMenuId);
      const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
      if (
        itemMenuRef
        && !itemMenuRef.contains(target)
        && !itemMenuButtonRef?.contains(target)
      ) {
        setOpenItemMenuId(null);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && openItemMenuId !== null) {
        const itemMenuButtonRef = itemMenuButtonRefs.current.get(openItemMenuId);
        setOpenItemMenuId(null);
        itemMenuButtonRef?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openItemMenuId]);

  const handleItemMenuToggle = (rowId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId((current) => (current === rowId ? null : rowId));
  };

  const handleItemMenuKeyDown = (rowId: string, event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId((current) => (current === rowId ? null : rowId));
      return;
    }

    if (event.key === 'Escape' && openItemMenuId === rowId) {
      event.preventDefault();
      event.stopPropagation();
      setOpenItemMenuId(null);
    }
  };

  const handleMenuAction = (action: () => void, event: React.MouseEvent) => {
    event.stopPropagation();
    setOpenItemMenuId(null);
    action();
  };

  const handleOpenResumo = (row: ListaSubestipulanteItem, event: React.MouseEvent) => {
    handleMenuAction(() => subestipulanteResumo.open(row), event);
  };

  const handleOpenInfo = (row: ListaSubestipulanteItem, event: React.MouseEvent) => {
    if (!onInfoSubEstipulante) {
      return;
    }
    handleMenuAction(() => onInfoSubEstipulante(row), event);
  };

  return (
    <>
      <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
        <div>
          <table
            className="min-w-[960px] w-full table-fixed"
            aria-label="Subestipulantes da apólice selecionada"
          >
            <thead>
              <tr>
                {hasRowActions && (
                  <th
                    className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16"
                    scope="col"
                  >
                    Ações
                  </th>
                )}
                <th className="px-3 py-2 text-left text-sm font-semibold border-b border-gray-100 w-[38%]" scope="col">
                  <Tooltip label="Código e nome do Sub-Estipulante">
                    <span>Nome</span>
                  </Tooltip>
                </th>
                <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-[10%]" scope="col">
                  <Tooltip label="Identificador do contrato">
                    <span>Contrato</span>
                  </Tooltip>
                </th>
                <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-[14%]" scope="col">
                  <Tooltip label="CPF ou CNPJ do Sub-Estipulante">
                    <span>Documento</span>
                  </Tooltip>
                </th>
                <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-[11%]" scope="col">
                  <Tooltip label="Data de início da vigência">
                    <span>Início vigência</span>
                  </Tooltip>
                </th>
                <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-[11%]" scope="col">
                  <Tooltip label="Data de fim da vigência">
                    <span>Fim vigência</span>
                  </Tooltip>
                </th>
                <th className="px-2 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-[10%]" scope="col">
                  <Tooltip label="Status do Sub-Estipulante">
                    <span>Status</span>
                  </Tooltip>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-50 text-text-color">
              {items && items.length > 0 ? (
                items.map((row) => {
                  const rowId = getRowMenuId(row);
                  const rowLabel = formatNomeComCodigo(row);

                  return (
                    <tr
                      key={rowId}
                      className="hover:bg-gray-50 transition-colors text-xs"
                    >
                      {hasRowActions && (
                        <td className="px-2 py-2 whitespace-nowrap text-left" onClick={(e) => e.stopPropagation()}>
                          <Tooltip label={`Ações do Sub-Estipulante ${rowLabel}`}>
                            <button
                              type="button"
                              ref={(el) => {
                                if (el) {
                                  itemMenuButtonRefs.current.set(rowId, el);
                                } else {
                                  itemMenuButtonRefs.current.delete(rowId);
                                }
                              }}
                              onClick={(e) => handleItemMenuToggle(rowId, e)}
                              onKeyDown={(e) => handleItemMenuKeyDown(rowId, e)}
                              className="inline-flex items-center justify-center rounded p-1 hover:bg-gray-100 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-gray-500 transition-colors"
                              aria-label={`Ações do Sub-Estipulante ${rowLabel}`}
                              aria-expanded={openItemMenuId === rowId}
                              aria-haspopup="menu"
                              aria-controls={`subestipulante-menu-${rowId}`}
                              aria-pressed={openItemMenuId === rowId}
                            >
                              <BsdsIcon icon={iconNavDots} className="w-3 h-3 text-gray-600" aria-hidden="true" />
                            </button>
                          </Tooltip>

                          <TableMenuPortal
                            isOpen={openItemMenuId === rowId}
                            anchorElement={itemMenuButtonRefs.current.get(rowId) ?? null}
                            menuRef={(el) => {
                              if (el) {
                                itemMenuRefs.current.set(rowId, el);
                              } else {
                                itemMenuRefs.current.delete(rowId);
                              }
                            }}
                            align="left"
                            id={`subestipulante-menu-${rowId}`}
                            className="min-w-[220px]"
                            aria-label={`Menu de Ações do Sub-Estipulante ${rowLabel}`}
                          >
                            {onViewSegurados && (
                              <button
                                type="button"
                                role="menuitem"
                                onClick={(e) => handleMenuAction(() => onViewSegurados(row), e)}
                                className={TABLE_MENU_ITEM_BUTTON_CLASS}
                              >
                                Visualizar Segurados
                              </button>
                            )}
                            {onViewProponentes && (
                              <button
                                type="button"
                                role="menuitem"
                                onClick={(e) => handleMenuAction(() => onViewProponentes(row), e)}
                                className={TABLE_MENU_ITEM_BUTTON_CLASS}
                              >
                                Visualizar Proponentes
                              </button>
                            )}
                            {hasSubEstipulanteDetailActions && (
                              <>
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={(e) => handleOpenInfo(row, e)}
                                  className={TABLE_MENU_ITEM_BUTTON_CLASS}
                                >
                                  Info SubEstipulante
                                </button>
                                <button
                                  type="button"
                                  role="menuitem"
                                  onClick={(e) => handleOpenResumo(row, e)}
                                  className={TABLE_MENU_ITEM_BUTTON_CLASS}
                                >
                                  Resumo SubEstipulante
                                </button>
                              </>
                            )}
                          </TableMenuPortal>
                        </td>
                      )}
                      <td className="px-3 py-2 align-top wrap-break-word text-gray-900">
                        {formatNomeComCodigo(row)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap align-top">{row.identContratoOut ?? '—'}</td>
                      <td className="px-2 py-2 whitespace-nowrap align-top">{formatDocumento(row.numDocumentoOut)}</td>
                      <td className="px-2 py-2 whitespace-nowrap align-top">{formatVigencia(row.dataIniVigenciaOut)}</td>
                      <td className="px-2 py-2 whitespace-nowrap align-top">{formatVigencia(row.dataFimVigenciaOut)}</td>
                      <td className="px-2 py-2 whitespace-nowrap align-top">{row.dsCstatus?.trim() || '—'}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnCount} className="px-4 py-4 text-center text-text-color text-xs">
                    {emptyMessage}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {onPageChange != null
        && currentPage != null
        && totalPages != null
        && totalItems != null
        && totalItems > 0 && (
          <div className="bsds-mt-16 bsds-mb-16">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={onPageChange}
              totalItems={totalItems}
              itemLabel="Sub-Estipulantes"
              alwaysShow
            />
          </div>
        )}

      <SubEstipulanteResumoModal
        subestipulanteResumo={subestipulanteResumo}
        numeroApolice={numeroApolice}
        onViewSegurados={onViewSegurados}
        onViewProponentes={onViewProponentes}
      />
    </>
  );
};

export default SubEstipulantesTable;
